import './App.scss';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { NavBar } from './Components/NavBar';
import { BpmRouter } from './Components/NavBar/BpmRouter';
import { FullPageSpinner } from './Components/Spinner';
import { ActivityFeedAgentDrawer } from './Pages/ActivityFeed/ActivityFeedAgentDrawer';
import { CrossChannelAgentDrawer } from './Pages/CrossChannel/CrossChannelAgentDrawer';
import { AgentDrawer } from './Pages/DashboardAgent/AgentDrawer';
import { Connect } from './Pages/Connect/Connect';
import { AuthUnavailableView } from './Pages/Login/AuthUnavailableView';
import { Login } from './Pages/Login/Login';
import { useAppSelector } from './redux';
import * as UserRedux from './redux/user';
import { captureIntendedDestination, oauthRedirectInProgress } from './utils/auth';
import { bootstrap } from './utils/auth/auth-client';
import { APP_URL, ConnectRouteMode, getConnectRouteMode } from './utils/auth/oauth-client';
import { ExperimentContext } from './utils/experiment-context';
import { useCompanyInfo } from './utils/hooks/useCompanyInfo';
import { useCompanyInfoFetch } from './utils/hooks/useCompanyInfoFetch';
import { Mixpanel } from './utils/mixpanelWrapper';

// /connect (the MCP sign-in consent page) and the redirect away from an issuer host never start
// an app session: no boot refresh and no destination capture, which would also stash /connect as
// the page to return to after login.
const connectRouteMode = getConnectRouteMode(window.location);

if (connectRouteMode === ConnectRouteMode.APP) {
  // Remember where the user was headed so login can return them there instead of the app root.
  // Runs before the auth check / any OAuth redirect so a deep link survives the login round-trip.
  captureIntendedDestination();

  // PLATFORM-988 boot refresh: one purge of legacy Amplify state plus one refresh (or null if
  // there's no session), fired at module load like the call above so it's already in flight by the
  // time useIsAuthed's effect fires — auth-client's single-flight de-dup collapses the two into one
  // POST /auth/refresh if they race. useIsAuthed owns what happens with the outcome (including the
  // retry view when the refresh endpoint is unavailable); this promise only exists to start the
  // request early, so a rejection is logged here and never left unhandled.
  bootstrap().catch(e => {
    console.error('Boot refresh failed; useIsAuthed will surface it', e);
  });
}

const MainApp = () => {
  useCompanyInfoFetch();
  const { cid, experiments: pgCompanyExperiments } = useCompanyInfo();

  const userEmail = useAppSelector(UserRedux.emailSelector);
  const userRole = useAppSelector(UserRedux.roleSelector);
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);

  const pgUserExperiments = useAppSelector(UserRedux.userExperimentsSelector);

  const experiments = useMemo(
    () => ({
      ...Object.fromEntries((pgCompanyExperiments || []).map(e => [e, true])),
      ...Object.fromEntries((pgUserExperiments || []).map(e => [e, true]))
    }),
    [pgCompanyExperiments, pgUserExperiments]
  );

  useEffect(() => {
    if (userEmail) {
      Mixpanel.identify(userEmail);
      Mixpanel.people.set({
        distinct_id: userEmail,
        $email: userEmail,
        role: userRole,
        isInternal,
        isAdmin,
        company: cid
      });
    }
  }, [userEmail, isInternal, isAdmin, userRole, cid]);

  return (
    <ExperimentContext.Provider value={experiments}>
      <NavBar />
      <div className='main'>
        <Outlet />
      </div>
      {(isInternal || experiments.enableDashboardBuilder) && <AgentDrawer />}
      {experiments.enableLLM && <CrossChannelAgentDrawer />}
      {experiments.enableLLM && <ActivityFeedAgentDrawer />}
    </ExperimentContext.Provider>
  );
};

const AppContent = () => {
  UserRedux.useVersionCheck();
  const isAuthed = UserRedux.useIsAuthed();

  const authUnavailable = useAppSelector(UserRedux.authUnavailableSelector);
  const retryAuth = UserRedux.useRetryAuth();

  // Keep the SAME BpmRouter + .bpm wrapper mounted for both the app and the login view, so switching
  // between them (login, logout) is a smooth in-place content swap rather than a full tree remount
  // that flashes. Gate on the resolved auth result, NOT cookie presence — cookies persist for 24h
  // and can linger after logout or an expired session. On logout the page stays on MainApp (a brief
  // "hang") until sign-out completes and isAuthed flips to false, then swaps to Login with no reload.
  // `initializing` spins the method's button while an auto-login resolves; a logged-out visitor has
  // no session, so nothing spins.
  // While auth is still resolving (initial /me check, or a load whose URL carries ?code=&state=,
  // which skips the boot check), show a neutral loader rather than the Login form. Otherwise
  // every authenticated page load briefly flashes the login page before isAuthed resolves to true
  // and MainApp mounts.
  // Only render Login once auth has definitively resolved to signed-out (isAuthed === false).
  const initializing = isAuthed == null || oauthRedirectInProgress;
  const renderContent = () => {
    // isAuthed is only true once /me answered 200, so the authorizer has already confirmed this
    // identity is entitled (a 403 there signs out; see useIsAuthed).
    if (isAuthed) return <MainApp />;
    // /auth/refresh or /me couldn't answer at boot: the session is untouched, offer a retry.
    if (authUnavailable) return <AuthUnavailableView onRetry={retryAuth} />;
    if (initializing) return <FullPageSpinner />;
    return <Login />;
  };

  return (
    <BpmRouter>
      <div className='bpm'>{renderContent()}</div>
    </BpmRouter>
  );
};

// The only place issuer-host traffic other than /connect goes: the app. Replaces rather than pushes
// so Back doesn't return to the issuer host.
const RedirectToApp = () => {
  useLayoutEffect(() => {
    window.location.replace(APP_URL);
  }, []);
  return <FullPageSpinner />;
};

export const App = () => {
  if (connectRouteMode === ConnectRouteMode.CONNECT) {
    return (
      <div className='bpm'>
        <Connect />
      </div>
    );
  }
  if (connectRouteMode === ConnectRouteMode.REDIRECT_TO_APP) return <RedirectToApp />;
  return <AppContent />;
};
